import { useState } from "react";
import { useRouter } from "expo-router";
import type { AppRouter } from "@ptsd/ptsd-manager-api";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import {
  createTRPCProxyClient,
  httpBatchLink,
  loggerLink,
  TRPCClientError,
} from "@trpc/client";
import { createTRPCReact } from "@trpc/react-query";
import superjson from "superjson";

import config from "~/config";

//TODO: move to utils package
export const isRetryableTRPCError = (error: unknown): boolean => {
  if (error instanceof TRPCClientError) {
    const statusCode = error.data?.httpStatus;

    if (statusCode === 500) return true;
    if (!statusCode) return true;
  }
  return false;
};

export const createVanillaTRPCClient = () =>
  createTRPCProxyClient<AppRouter>({
    transformer: superjson,
    links: [
      loggerLink({
        enabled: (opts) =>
          process.env.NODE_ENV === "development" ||
          (opts.direction === "down" && opts.result instanceof Error),
        colorMode: "ansi",
      }),
      httpBatchLink({
        url: `${config.API_URL}/trpc`,
        fetch(url, options) {
          return fetch(url, {
            ...options,
            credentials: "include",
          });
        },
      }),
    ],
  });

/**
 * A set of typesafe hooks for consuming your API.
 */
export const api = createTRPCReact<AppRouter>();
export { type RouterInputs, type RouterOutputs } from "@ptsd/ptsd-manager-api";

export const vanillaApi = createVanillaTRPCClient();

// const getBaseUrl = () => {
//   /**
//    * Gets the IP address of your host-machine. If it cannot automatically find it,
//    * you'll have to manually set it. NOTE: Port 3000 should work for most but confirm
//    * you don't have anything else running on it, or you'd have to change it.
//    *
//    * **NOTE**: This is only for development. In production, you'll want to set the
//    * baseUrl to your production API URL.
//    */
//   // const debuggerHost = Constants.expoConfig?.hostUri;
//   // const localhost = debuggerHost?.split(":")[0];
//
//   if (process.env.NODE_ENV === "production") {
//     // temporary workaround while we have only one environment.make it as env later
//     return "https://develop.ptsd.team";
//   }
//   return `http://localhost:4000`;
// };

/**
 * A wrapper for your app that provides the TRPC context.
 * Use only in _app.tsx
 */

export function TRPCProvider(props: { children: React.ReactNode }) {
  const router = useRouter();

  const [queryClient] = useState(
    () =>
      new QueryClient({
        defaultOptions: {
          queries: {
            retry: (failureCount, error) => {
              return failureCount < 3 && isRetryableTRPCError(error);
            },
            retryDelay: (attemptIndex) =>
              Math.min(1000 * 2 ** attemptIndex, 30000),
          },
        },
      }),
  );

  const [trpcClient] = useState(() =>
    api.createClient({
      transformer: superjson,
      links: [
        httpBatchLink({
          url: `${config.API_URL}/trpc`,
          async fetch(url, options) {
            const result = await fetch(url, {
              ...options,
              credentials: "include", //TODO: should be env parameter?
            });

            if (result.status === 401) {
              router.replace("/auth");
            }

            return result;
          },
        }),
        loggerLink({
          enabled: (opts) =>
            process.env.NODE_ENV === "development" ||
            (opts.direction === "down" && opts.result instanceof Error),
          colorMode: "ansi",
        }),
      ],
    }),
  );

  return (
    <api.Provider client={trpcClient} queryClient={queryClient}>
      <QueryClientProvider client={queryClient}>
        {props.children}
      </QueryClientProvider>
    </api.Provider>
  );
}
